import { type GetBackNotificheParams } from '@framework/api-client/api/backoffice/schemas/getBackNotificheParams.ts';
import { type GetFrontNotificheParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontNotificheParams.ts';
import { SortByNotifica } from '@framework/api-client/api/inquiries-compile/schemas/sortByNotifica.ts';
import { SortDirection } from '@framework/api-client/api/inquiries-compile/schemas/sortDirection.ts';
import { AuthHeaderFlags } from '@framework/auth/http/AuthHeaderFlags.tsx';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useInterval } from 'usehooks-ts';
import { useNotificationsContext } from '../context/NotificationsContext.tsx';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_NOTIFICATION_FLAG_MS_DELAY = 120 * 1000;
const NOTIFICATION_FLAG_QUERY = {
  flVista: false,
  sortField: SortByNotifica.DATA_NOTIFICA,
  sortDirection: SortDirection.DESC,
  firstResult: 0,
  maxResults: 1,
};

/**
 * Rimappa `tipoDomandaId` per evitare che React Hook Form interpreti
 * il punto come path annidato nel parametro
 */
function remapQueryParams(
  query: GetFrontNotificheParams | GetBackNotificheParams,
): GetFrontNotificheParams | GetBackNotificheParams {
  if ('tipoDomandaId' in query && query.tipoDomandaId) {
    const { tipoDomandaId, ...rest } = query as any;
    return { ...rest, 'tipoDomanda.id': tipoDomandaId };
  }
  return query;
}

/**
 * Hook che ritorna le notifiche utente.
 * Le notifiche vengono riaggiornate ogni `delay` millisecondi.
 * Se stop è true, le notifiche non vengono aggiornate
 */
export function useNotification(
  query: GetFrontNotificheParams | GetBackNotificheParams,
) {
  const { useGetNotifications, getGetNotificationsQueryKey } =
    useNotificationsContext();

  const normalizedQuery = useMemo(() => {
    return remapQueryParams(query);
  }, [query]);

  const apiResult = useGetNotifications(normalizedQuery, {
    query: {
      keepPreviousData: true,
    },
  });
  const notificationUpdate = useNotificationUpdate();

  // Invalido la key e refetch delle notifiche ad intervalli regolari
  const queryClient = useQueryClient();
  const refetch = useCallback(() => {
    queryClient.invalidateQueries(getGetNotificationsQueryKey(normalizedQuery));
  }, [normalizedQuery, queryClient, getGetNotificationsQueryKey]);

  const notifications = apiResult.data?.data?.list;

  const pageSize = query.maxResults ?? DEFAULT_PAGE_SIZE;
  const pageNumber =
    Math.floor((query.firstResult ?? 0) / query.maxResults!) + 1;

  const meta = {
    pageSize,
    pageNumber,
    total: apiResult.data?.data?.totalRecs,
  };

  return {
    apiResult,
    notifications,
    notificationUpdate,
    meta,
    refetch,
  };
}

/**
 * Gestisce il meccanismo che indica quando c'è almeno una nuova notifica non letta.
 * Salva le informazioni nel localStorage così che siano sempre disponibili.
 */
function useNotificationUpdate() {
  const { t } = useTranslation();
  //  Seleziono solo l'ultima notifica creata
  const [unreadTotal, setUnreadTotal] = useState(0);
  const { getNotifications, useGetNotifications } = useNotificationsContext();

  const apiResult = useGetNotifications(NOTIFICATION_FLAG_QUERY, {
    request: {
      headers: {
        // Non dobbiamo rinnovare la sessione quando siamo in polling
        [AuthHeaderFlags.IsPolling]: 'true',
      },
    },
  });
  const notification = apiResult.data?.data?.list?.[0];
  //const unread = localStorage.getItem(READ_NOTIFICATION_STORAGE_KEY) === '1';

  // Resetto il flag di unread per non mostrare più il badge
  const resetUnreadFlag = useCallback(async () => {
    try {
      const result = await getNotifications(NOTIFICATION_FLAG_QUERY);
      setUnreadTotal(result.data?.totalRecs ?? 0);
    } catch (error) {
      message.error(
        t(
          'error-while-resetting-notification-flag',
          'Errore durante il reset del flag di notifica',
        ),
      );
      console.error('Errore durante il reset del flag di notifica', { error });
    }
    // TODO/FWP Verificare utilità della dipendenza
  }, [localStorage]);

  // Invalido la key e refetch delle notifiche ad intervalli regolari
  useInterval(() => {
    apiResult.refetch();
  }, DEFAULT_NOTIFICATION_FLAG_MS_DELAY);

  // Ad ogni aggiornamento della notifica, aggiorno coerentemente anche il flag
  useEffect(() => {
    setUnreadTotal(apiResult.data?.data?.totalRecs ?? 0);
  }, [notification]);

  return {
    unreadTotal,
    resetUnreadFlag,
  };
}
